import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';

// ── Cache ────────────────────────────────────────────────────────────────────
/**
 * In-session cache for the resolved module access map.
 * Stores a Promise so concurrent callers await the same fetch.
 *
 * TTL: 30 seconds — after which the next call forces a re-fetch.
 * This ensures that SA changes propagate to the tenant side within 30s
 * without requiring a full page reload.
 *
 * Call clearModuleCache() to force immediate invalidation.
 */
let _cache: Promise<Record<string, boolean> | null> | null = null;
let _cacheTs = 0;
const CACHE_TTL_MS = 30_000;

/* ─── Internal loader ───────────────────────────────────────── */

async function loadModuleMap(): Promise<Record<string, boolean> | null> {
  const tenantId = await getTenantId();

  // Super Admin without impersonation — unrestricted access
  if (!tenantId) return null;

  const { data: tenantData, error: tenantErr } = await supabase
    .from('saas_tenants')
    .select('plan_code')
    .eq('id', tenantId)
    .maybeSingle();

  if (tenantErr || !tenantData) {
    console.warn('[hasModuleAccess] Could not resolve tenant plan:', tenantErr?.message);
    return {};
  }

  const [pmRes, tmRes] = await Promise.all([
    supabase
      .from('saas_plan_modules')
      .select('module_id, is_included')
      .eq('plan_id', tenantData.plan_code),
    supabase
      .from('tenant_modules')
      .select('module_id, is_enabled')
      .eq('tenant_id', tenantId),
  ]);

  const map: Record<string, boolean> = {};

  // Step 1 — apply plan defaults
  ((pmRes.data ?? []) as { module_id: string; is_included: boolean }[]).forEach((r) => {
    map[r.module_id] = r.is_included;
  });

  // Step 2 — apply tenant-level overrides (take precedence over plan)
  ((tmRes.data ?? []) as { module_id: string; is_enabled: boolean }[]).forEach((r) => {
    map[r.module_id] = r.is_enabled;
  });

  return map;
}

/** Returns a fresh loader promise and stamps the cache timestamp */
function freshLoad(): Promise<Record<string, boolean> | null> {
  _cacheTs = Date.now();
  _cache = loadModuleMap();
  return _cache;
}

/* ─── Cache guard ───────────────────────────────────────────── */

function getCache(): Promise<Record<string, boolean> | null> {
  const expired = Date.now() - _cacheTs > CACHE_TTL_MS;
  if (!_cache || expired) return freshLoad();
  return _cache;
}

/* ─── Public API ────────────────────────────────────────────── */

/**
 * Returns true if the current user's tenant has access to the given module.
 *
 * Resolution order:
 *   1. tenant_modules override (if exists) → use is_enabled
 *   2. saas_plan_modules for the tenant's plan → use is_included
 *   3. Default → false
 *
 * Super Admins (no tenant context) always get true.
 * Result is cached for 30s — call clearModuleCache() to reset immediately.
 */
export async function hasModuleAccess(moduleId: string): Promise<boolean> {
  const map = await getCache();
  if (map === null) return true; // SA → full access
  return map[moduleId] ?? false;
}

/**
 * Check multiple modules at once.
 * Returns a Record<moduleId, boolean>.
 */
export async function getModuleAccessMap(
  moduleIds: string[]
): Promise<Record<string, boolean>> {
  const map = await getCache();

  if (map === null) {
    return moduleIds.reduce<Record<string, boolean>>((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }

  return moduleIds.reduce<Record<string, boolean>>((acc, id) => {
    acc[id] = map[id] ?? false;
    return acc;
  }, {});
}

/**
 * Returns the list of all currently enabled module IDs for this tenant.
 */
export async function getEnabledModules(): Promise<string[]> {
  const map = await getCache();
  if (map === null) return [];
  return Object.entries(map)
    .filter(([, enabled]) => enabled)
    .map(([id]) => id);
}

/**
 * Clears the in-memory cache immediately.
 * Call this on:
 *   - User logout
 *   - Plan upgrade/downgrade
 *   - Tenant impersonation change
 *   - After SA approves a module request
 */
export function clearModuleCache(): void {
  _cache = null;
  _cacheTs = 0;
}

/**
 * Force-refresh: clears cache and immediately reloads.
 * Returns the fresh access map.
 */
export async function refreshModuleCache(): Promise<Record<string, boolean> | null> {
  clearModuleCache();
  return freshLoad();
}

/**
 * Patches a single module in the current in-memory cache without a full reload.
 * Useful after the SA approves a request and you want the tenant UI to reflect
 * the change instantly without waiting for the next TTL expiry.
 *
 * If cache is empty or expired, this is a no-op (next getCache() will reload).
 */
export async function patchModuleCache(moduleId: string, value: boolean): Promise<void> {
  if (!_cache) return;
  const map = await _cache;
  if (map === null) return; // SA — unrestricted, no-op
  map[moduleId] = value;
}